// Designer-side TxnRuntime: fills the shared transaction-form transport
// (@twasta/page-runtime/txn) with the live-preview endpoints + document
// store + react-hot-toast. This preserves exactly how the designer/live
// preview and deployed-site embeds behave today — the lookup picker and
// "add new master" still go through /api/livepreview/*; only document
// upload varies per call site (site embeds inject their own uploader).

import toast from 'react-hot-toast'
import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import {
  livePreviewDeploy, livePreviewLookup, livePreviewLookupSource,
  livePreviewResolveDefaults, livePreviewSave, livePreviewRunAction,
  livePreviewUploadContentImage,
} from '../api'
import { uploadDocument as uploadDocumentToStore } from '@/api/documentStore'
import { makeReportRuntimeAdapter } from '@/pages/designers/report/ReportLivePreviewPane'
import { AssistantActionModal } from '@/assistant/AssistantActionModal'
import { PaymentActionModal } from './PaymentActionModal'
import { ImportActionWindow } from '@/assistant/ImportActionWindow'
import { useProjectStore } from '@/store/projectStore'
import { platformExportTransport } from '@/api/txnExport'
import type {
  TxnRuntime, UploadResult, RunActionRequest, AssistantActionRequest,
  PaymentActionRequest, ImportActionRequest, WizardActionRequest, SendActionRequest,
} from '@twasta/page-runtime/txn'
import { parseApiError } from '@twasta/page-runtime/txn'
import type { WizardVia } from '@twasta/page-runtime/wizard/contract'

// handler='assistant' buttons open a self-contained Assistant modal. Mounted
// imperatively (own React root) so EVERY surface that builds this runtime —
// live preview, in-app Run, embeds — gets it without threading modal state
// through each host. The modal needs no React context (plain styles + the
// module-level API client).
export function openAssistantActionModal(project: string, req: AssistantActionRequest): void {
  const holder = document.createElement('div')
  document.body.appendChild(holder)
  const root = createRoot(holder)
  const close = () => { root.unmount(); holder.remove() }
  root.render(createElement(AssistantActionModal, { project, req, onClose: close }))
}

// handler='payment' buttons open the application's own checkout the same
// imperative way. The payment endpoints are keyed by the project's OWNER —
// storage is per-creator — and on a shared project that is someone other
// than the person clicking, so it is read from the resolved project access
// rather than assumed to be the current user.
export function openPaymentActionModal(project: string, req: PaymentActionRequest): void {
  const ownerId = useProjectStore.getState().projectAccess?.owner_user_id || ''
  const holder = document.createElement('div')
  document.body.appendChild(holder)
  const root = createRoot(holder)
  const close = () => { root.unmount(); holder.remove() }
  root.render(createElement(PaymentActionModal, { project, ownerId, req, onClose: close }))
}

// handler='import' buttons open the file-import window the same imperative
// way: pick a file, map its columns, then the Assistant's side-by-side
// preview — so every surface building this runtime gets it.
export function openImportActionModal(project: string, req: ImportActionRequest): void {
  const holder = document.createElement('div')
  document.body.appendChild(holder)
  const root = createRoot(holder)
  const close = () => { root.unmount(); holder.remove() }
  root.render(createElement(ImportActionWindow, { project, req, onClose: close }))
}

// handler='send' buttons open the send window (preview with the default
// template → compose → progress), and a record's "Messages" item its log —
// loaded on first use (TipTap + the composer are not needed until then).
export function openSendActionModal(project: string, req: SendActionRequest): void {
  void import('@/messaging/SendActionWindowHost')
    .then((m) => m.openSendActionModal(project, req))
    .catch((err) => toast.error(parseApiError(err).message || 'The send window could not be opened'))
}

export function openMessageLogModal(project: string, objName: string, txnId: string): void {
  void import('@/messaging/SendActionWindowHost')
    .then((m) => m.openMessageLogModal(project, objName, txnId))
    .catch((err) => toast.error(parseApiError(err).message || 'The message log could not be opened'))
}

// handler='wizard' buttons open a Transaction Wizard in a slide-over, the same
// imperative way — so every surface building this runtime gets one even when
// its host cannot navigate. The drawer module (and the runner behind it) is
// loaded on first use: it imports this module's screen runtime, and most
// screens never open a wizard.
export function openWizardDrawer(project: string, req: WizardActionRequest): void {
  void import('./wizardDrawer')
    .then((m) => m.mountWizardDrawer(project, req))
    .catch((err) => toast.error(parseApiError(err).message || 'The wizard could not be opened'))
}

export function makeDesignerTxnRuntime(
  project: string,
  uploadDocument?: (file: File) => Promise<UploadResult>,
  // Surface-aware override (deployed-site embeds route run_action to /site-txn).
  // Defaults to the author-only live-preview endpoint.
  runAction?: TxnRuntime['runAction'],
  opts: {
    /** Every request runs under this Transaction Wizard (design §12.2): the
     *  lookup / defaults / save / run_action bodies carry it, and the server
     *  authorizes them against the wizard's grant instead of the base T's. */
    wizard?: WizardVia
    /** handler='wizard' buttons. Absent → the slide-over (openWizardDrawer). */
    openWizard?: TxnRuntime['openWizardAction']
  } = {},
): TxnRuntime {
  const via = opts.wizard
  return {
    lookup: via
      ? (proj, objName, lookup, search, limit, lookupOpts) =>
        livePreviewLookup(proj, objName, lookup, search, limit, { ...lookupOpts, wizard: via })
      : livePreviewLookup,
    lookupSource: livePreviewLookupSource,
    resolveDefaults: via
      ? (proj, objName) => livePreviewResolveDefaults(proj, objName, { wizard: via })
      : livePreviewResolveDefaults,
    save: via
      ? (proj, objName, header, txnId, saveOpts) =>
        livePreviewSave(proj, objName, header, txnId, { ...saveOpts, wizard: via })
      : livePreviewSave,
    deploy: livePreviewDeploy,
    runAction: runAction
      ?? ((proj: string, objName: string, req: RunActionRequest) =>
        livePreviewRunAction(proj, objName, req, via ? { wizard: via } : undefined)),
    uploadDocument:
      uploadDocument
      ?? (async (file: File) => {
        const doc = await uploadDocumentToStore(project, file)
        return { url: doc.url, name: doc.name }
      }),
    // Images inside a RICHTEXT field (Run view / designer preview). A caller
    // that injects its own uploader is a deployed-site embed, whose visitor
    // holds no platform session for /api/livepreview — it gets insert-by-URL.
    ...(uploadDocument ? {} : {
      uploadContentImage: async (proj: string, objName: string, file: File) => {
        try {
          return await livePreviewUploadContentImage(proj, objName, file)
        } catch (err) {
          throw new Error(parseApiError(err).message || 'Image upload failed')
        }
      },
    }),
    // handler='report' action buttons open the bound Jasper report over the
    // platform's /api/report-data endpoints.
    makeReportAdapter: (reportObjName: string) =>
      makeReportRuntimeAdapter(project, reportObjName),
    // handler='assistant' action buttons open the Assistant modal (intent runs
    // deterministically via /api/assistant/run-intent; prompt mode seeds a turn).
    openAssistantAction: (req) => openAssistantActionModal(project, req),
    // handler='payment' action buttons open the application's OWN checkout
    // (/api/app-payments) — the rail where the project owner is paid.
    openPaymentAction: (req) => openPaymentActionModal(project, req),
    // handler='import' action buttons open the file-import window
    // (/api/record-import → the shared ImportPreview).
    openImportAction: (req) => openImportActionModal(project, req),
    // handler='wizard' action buttons: the host navigates to the wizard when
    // it can (the Run view), otherwise it opens in a slide-over.
    openWizardAction: opts.openWizard ?? ((req) => openWizardDrawer(project, req)),
    // handler='send' action buttons (/api/messaging → the shared send window),
    // and the row / editor "Messages" item.
    openSendAction: (req) => openSendActionModal(project, req),
    openMessageLog: (objName, txnId) => openMessageLogModal(project, objName, txnId),
    // The Export system action (/api/txn-export → the shared Export window and
    // template manager). Not on a deployed-site embed (its visitor holds no
    // platform session) nor under a wizard (a wizard writes one record).
    ...(uploadDocument || via ? {} : { exportApi: platformExportTransport(project) }),
    notify: {
      success: (m: string) => toast.success(m),
      error: (m: string) => toast.error(m),
      info: (m: string) => toast(m, { icon: 'ℹ️' }),
      warn: (m: string) => toast(m, { icon: '⚠️' }),
    },
  }
}
